import { useMemo } from 'react';
import {
  ACCESS_TOKEN_KEY,
  IMPERSONATE_ACCESS_TOKEN_KEY,
  IMPERSONATE_REFRESH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  STORAGE_ACCESS_TOKEN_KEY,
  STORAGE_IMPERSONATE_ACCESS_TOKEN_KEY,
  STORAGE_IMPERSONATE_REFRESH_TOKEN_KEY,
  STORAGE_REFRESH_TOKEN_KEY,
} from '@utils/token/constants';
import { getAccessTokenFromStorage } from '@utils/token/get-access-token-from-storage';
import { getRefreshTokenFromStorage } from '@utils/token/get-refresh-token-from-storage';
import { removeToken } from '@utils/token/remove-token';
import { setAccessToken } from '@utils/token/set-access-token';
import { setRefreshToken } from '@utils/token/set-refresh-token';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

/**
 * Returns a STABLE object: nothing in here closes over reactive state — every function
 * reads cookies and storage at call time — so the identities can live for the lifetime
 * of the component.
 *
 * That matters beyond tidiness. These functions end up in the dependency arrays of
 * `AuthProvider`'s callbacks, which end up in its context value; while they were
 * recreated on every render, every `useMemo` and `useCallback` downstream was
 * decorative — the pattern satisfied Sonar S6481 and re-rendered every consumer of
 * `AuthContext` anyway (B2B-2142).
 */
export const useTokenManagement = () =>
  useMemo(() => {
    const getTokens = ({ isImpersonate = false }: { isImpersonate?: boolean } = {}) => {
      const accessCookieKey = isImpersonate ? IMPERSONATE_ACCESS_TOKEN_KEY : ACCESS_TOKEN_KEY;
      const refreshCookieKey = isImpersonate ? IMPERSONATE_REFRESH_TOKEN_KEY : REFRESH_TOKEN_KEY;
      const accessStorageKey = isImpersonate ? STORAGE_IMPERSONATE_ACCESS_TOKEN_KEY : STORAGE_ACCESS_TOKEN_KEY;
      const refreshStorageKey = isImpersonate ? STORAGE_IMPERSONATE_REFRESH_TOKEN_KEY : STORAGE_REFRESH_TOKEN_KEY;

      let accessToken = Cookies.get(accessCookieKey as string);
      let refreshToken = Cookies.get(refreshCookieKey as string);

      // Fallback: if a cookie is missing, try reading from browser storage.
      // When found, restore the cookie so subsequent operations (e.g. request() Authorization header) work.
      if (!accessToken) {
        const storedAccess = getAccessTokenFromStorage(accessStorageKey as string);
        if (storedAccess) {
          setAccessToken({ key: accessCookieKey as string, token: storedAccess });
          accessToken = storedAccess;
        }
      }

      if (!refreshToken) {
        const storedRefresh = getRefreshTokenFromStorage(refreshStorageKey as string);
        if (storedRefresh) {
          setRefreshToken({ key: refreshCookieKey as string, token: storedRefresh });
          refreshToken = storedRefresh;
        }
      }

      return { accessToken, refreshToken };
    };

    const setTokens = ({
      accessToken,
      refreshToken,
      isImpersonate,
    }: {
      accessToken?: string;
      refreshToken: string;
      isImpersonate?: boolean;
    }) => {
      if (isImpersonate) {
        accessToken && setAccessToken({ key: IMPERSONATE_ACCESS_TOKEN_KEY, token: accessToken });
        setRefreshToken({ key: IMPERSONATE_REFRESH_TOKEN_KEY, token: refreshToken });
      } else {
        accessToken && setAccessToken({ key: ACCESS_TOKEN_KEY, token: accessToken });
        setRefreshToken({ key: REFRESH_TOKEN_KEY, token: refreshToken });
      }
    };

    const clearTokens = ({ isImpersonate = false }: { isImpersonate?: boolean } = {}) => {
      if (!isImpersonate) {
        removeToken(REFRESH_TOKEN_KEY as string);
        removeToken(ACCESS_TOKEN_KEY as string);
      }
      removeToken(IMPERSONATE_ACCESS_TOKEN_KEY as string);
      removeToken(IMPERSONATE_REFRESH_TOKEN_KEY as string);
    };

    const getIsAdmin = ({ isImpersonate = false }: { isImpersonate?: boolean } = {}) => {
      const accessToken = getTokens({ isImpersonate }).accessToken;
      const decodedToken: { isAdmin: boolean } = accessToken ? jwtDecode(accessToken) : { isAdmin: false };

      return decodedToken.isAdmin;
    };

    return {
      getIsAdmin,
      getTokens,
      setTokens,
      clearTokens,
    };
  }, []);
